export const MAP_EXPLORATION_RADIUS_METERS = 15;
export const MAP_EXPLORATION_INTRO_STORAGE_KEY = "qubiq_map_exploration_intro_seen";
export const MAP_EXPLORATION_LOCATION_STORAGE_KEY = "qubiq_map_exploration_last_location";
export const MAP_EXPLORATION_GEO_REQUESTED_STORAGE_KEY =
  "qubiq_map_exploration_geo_requested";
export const MAP_EXPLORATION_ACTIVE_TREASURE_HUNT_RUN_STORAGE_KEY =
  "qubiq_map_exploration_active_treasure_hunt_run_id";

export const MAP_POINT_COLORS = {
  viewed: {
    fill: "#7c8592",
    glow: "rgba(124, 133, 146, 0.24)",
    ring: "#7c8592",
  },
  private: {
    fill: "#25c36f",
    glow: "rgba(37, 195, 111, 0.28)",
    ring: "#25c36f",
  },
  unviewed: {
    fill: "#ff3b30",
    glow: "rgba(255, 59, 48, 0.28)",
    ring: "#ff3b30",
  },
  badge: {
    fill: "#101116",
    text: "#fff",
    border: "rgba(255,255,255,0.15)",
  },
  markerShadow: {
    strong: "0 10px 18px rgba(0,0,0,0.22)",
    soft: "0 8px 14px rgba(0,0,0,0.18)",
    faint: "0 6px 10px rgba(0,0,0,0.14)",
  },
  imageOverlay: {
    viewed: "rgba(255,255,255,0.18)",
    inaccessible: "rgba(40,44,52,0.14)",
  },
};

export const MAP_POINT_CUBE_COLORS = {
  viewed: {
    front: "#d8bc58",
    top: "#efd77b",
    side: "#b99630",
    border: "#000000",
    glow: "rgba(216, 188, 88, 0.26)",
    base: "rgba(153, 118, 27, 0.38)",
  },
  private: {
    front: "#ffcf00",
    top: "#ffe27a",
    side: "#c99200",
    border: "#000000",
    glow: "rgba(184, 146, 22, 0.3)",
    base: "rgba(138, 101, 4, 0.42)",
  },
  unviewed: {
    front: "#ffcf00",
    top: "#ffe27a",
    side: "#c99200",
    border: "#000000",
    glow: "rgba(184, 146, 22, 0.3)",
    base: "rgba(138, 101, 4, 0.42)",
  },
};

export function getMapPointVisual(state) {
  return MAP_POINT_COLORS[state] || MAP_POINT_COLORS.unviewed;
}

export function getMapPointCubeVisual(state) {
  return MAP_POINT_CUBE_COLORS[state] || MAP_POINT_CUBE_COLORS.unviewed;
}

export function getMapPointBadgeVisual() {
  return MAP_POINT_COLORS.badge;
}

export function getMapPointShadowVisual() {
  return MAP_POINT_COLORS.markerShadow;
}

export function getMapPointImageOverlayVisual() {
  return MAP_POINT_COLORS.imageOverlay;
}

export function getMapPointMarkerOpacity(state) {
  if (state === "viewed") return 1;
  if (state === "private") return 0.96;
  return 1;
}

export function getMapPointImageOpacity(state) {
  if (state === "viewed") return 1;
  if (state === "private") return 0.94;
  return 1;
}

export function isViewedMapPointState(state) {
  return state === "viewed";
}

export function isInaccessibleMapPointState(state) {
  return state === "unviewed" || state === "private";
}

export function getMapPointColorState(params = {}) {
  if (params.allViewed) return "viewed";
  if (params.hasPrivateContent) return "private";
  return "unviewed";
}

export function getMapPointMarkerKind(params = {}) {
  const videoCount = Math.max(0, Number(params.videoCount || 0));
  if (videoCount !== 1) return "cube";

  if (params.isTreasureHuntEntryExplore && params.treasureHuntThumbnailUrl) {
    return "treasureHuntThumbnail";
  }

  if (params.channelIconUrl) {
    return "channelAvatar";
  }
  return "cube";
}

export function formatNearbyCountLabel(count) {
  const safeCount = Math.max(0, Number(count) || 0);
  if (safeCount > 99) return "99+";
  if (safeCount > 9) return "9+";
  return String(safeCount);
}

export function getStoredMapExplorationLocation() {
  if (typeof window === "undefined") return null;

  try {
    const rawValue = window.localStorage.getItem(
      MAP_EXPLORATION_LOCATION_STORAGE_KEY
    );
    if (!rawValue) return null;
    const parsed = JSON.parse(rawValue);
    if (
      !Number.isFinite(Number(parsed?.latitude)) ||
      !Number.isFinite(Number(parsed?.longitude))
    ) {
      return null;
    }

    return {
      latitude: Number(parsed.latitude),
      longitude: Number(parsed.longitude),
      updatedAt: parsed.updatedAt || null,
    };
  } catch {
    return null;
  }
}

export function storeMapExplorationLocation(location) {
  if (
    typeof window === "undefined" ||
    !Number.isFinite(Number(location?.latitude)) ||
    !Number.isFinite(Number(location?.longitude))
  ) {
    return;
  }

  window.localStorage.setItem(
    MAP_EXPLORATION_LOCATION_STORAGE_KEY,
    JSON.stringify({
      latitude: Number(location.latitude),
      longitude: Number(location.longitude),
      updatedAt: location.updatedAt || new Date().toISOString(),
    })
  );
}

export function hasRequestedMapExplorationGeolocation() {
  if (typeof window === "undefined") return false;
  return (
    window.localStorage.getItem(MAP_EXPLORATION_GEO_REQUESTED_STORAGE_KEY) === "1"
  );
}

export function markMapExplorationGeolocationRequested() {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(MAP_EXPLORATION_GEO_REQUESTED_STORAGE_KEY, "1");
}

export function hasSeenMapExplorationIntro() {
  if (typeof window === "undefined") return false;
  return window.localStorage.getItem(MAP_EXPLORATION_INTRO_STORAGE_KEY) === "1";
}

export function markMapExplorationIntroSeen() {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(MAP_EXPLORATION_INTRO_STORAGE_KEY, "1");
}

export function getStoredActiveTreasureHuntRunId() {
  if (typeof window === "undefined") return 0;

  const rawValue = window.localStorage.getItem(
    MAP_EXPLORATION_ACTIVE_TREASURE_HUNT_RUN_STORAGE_KEY
  );
  const runId = Math.floor(Number(rawValue || 0));
  return Number.isFinite(runId) && runId > 0 ? runId : 0;
}

export function storeActiveTreasureHuntRunId(runId) {
  if (typeof window === "undefined") return;

  const normalizedRunId = Math.floor(Number(runId || 0));
  if (!Number.isFinite(normalizedRunId) || normalizedRunId <= 0) {
    window.localStorage.removeItem(
      MAP_EXPLORATION_ACTIVE_TREASURE_HUNT_RUN_STORAGE_KEY
    );
    return;
  }

  window.localStorage.setItem(
    MAP_EXPLORATION_ACTIVE_TREASURE_HUNT_RUN_STORAGE_KEY,
    String(normalizedRunId)
  );
}

export function clearStoredActiveTreasureHuntRunId() {
  if (typeof window === "undefined") return;
  window.localStorage.removeItem(
    MAP_EXPLORATION_ACTIVE_TREASURE_HUNT_RUN_STORAGE_KEY
  );
}

export function appendStoredMapLocationParams(input, location = getStoredMapExplorationLocation()) {
  const rawInput = String(input || "");
  if (!rawInput) return rawInput;
  if (
    !location ||
    !Number.isFinite(Number(location.latitude)) ||
    !Number.isFinite(Number(location.longitude))
  ) {
    return rawInput;
  }

  const isAbsoluteUrl = /^https?:\/\//i.test(rawInput);
  const baseOrigin =
    typeof window !== "undefined" && window.location?.origin
      ? window.location.origin
      : "http://localhost";

  try {
    const url = isAbsoluteUrl ? new URL(rawInput) : new URL(rawInput, baseOrigin);
    url.searchParams.set("latitude", String(Number(location.latitude)));
    url.searchParams.set("longitude", String(Number(location.longitude)));

    if (isAbsoluteUrl) {
      return url.toString();
    }

    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return rawInput;
  }
}
